/**
 * SSM (Semantic Source Merge) body content guard — row 0.99200.
 *
 * Companion to the existing `degradedMode` banner stamped on
 * `SiteRagReport` when AI narrative generation falls back to the
 * deterministic-only pipeline. The banner warns the READER that the
 * narrative was assembled without full evidence. SSM-BODY-GUARD adds
 * the same warning at the SENTENCE level: any inline `[Ref N]`
 * citation whose appendix entry has NO provider evidence (URL is
 * blank and the source string matches the "inferred from context"
 * sentinel set) is rewritten to be preceded by an `[unverified]`
 * reliability token. `walkClaimText` then renders the chip — no
 * changes to the renderer are required, the token is the same shape
 * the existing parser already understands.
 *
 * Behaviour matrix (all three must hold for the rewrite to fire):
 *
 *   degradedMode = true   AND
 *   VITE_SSM_BODY_GUARD   AND
 *   ref is inferred-from-context
 *
 * Outside any of these, the input text is returned untouched (object-
 * identity preserved when possible, so memoisation downstream stays
 * stable). Tokens that ALREADY carry an inline reliability marker
 * (`[verified Ref 3]`, `[inferred Ref 3]`, `[unverified Ref 3]`) are
 * skipped — the LLM's own marking wins.
 *
 * The "inferred from context" heuristic is intentionally conservative
 * — it only matches refs that BOTH (a) lack a URL and (b) whose
 * `source` string contains one of the well-known sentinels. A real
 * provider row (with a URL) is never flagged even if its source
 * string happens to contain the word "baseline" (e.g. a USGS
 * baseline-flow dataset). The heuristic CAN be widened later via a
 * structured `DataSource.kind` field, but until that ships this
 * conservative rule keeps false-positives near-zero.
 */

import type { DataSource, SiteRagReport } from '../types/siteRag';
import { isSsmBodyGuardEnabled } from './runtimeFlags';

/**
 * Sentinels surfaced by the deterministic fallback when it stamps
 * an evidence row WITHOUT a provider URL. Matched case-insensitively
 * against `DataSource.source`. Includes the exact strings emitted by
 * `siteRagService` / `withEmptyNarrativeRetry` plus the broader
 * vocabulary the LLM uses when it labels its own inference.
 */
const INFERRED_FROM_CONTEXT_SENTINELS = [
  'inferred',
  'inference',
  'no provider',
  'regional baseline',
  'industry baseline',
  'industry benchmark',
  'training corpus',
  'training data',
  'derived from context',
  'context-derived',
  'context derived',
  'best-estimate',
  'best estimate',
];

/**
 * Predicate: is this appendix row evidence-light enough that any
 * claim citing it inside a degraded report should be chipped as
 * unverified? See module docstring for the conservative rule —
 * BOTH no-url AND a sentinel match.
 */
export function isInferredFromContextRef(ref: DataSource | undefined): boolean {
  if (!ref) return false;
  if (typeof ref.url === 'string' && ref.url.trim().length > 0) return false;
  const src = (ref.source ?? '').toLowerCase();
  if (src.length === 0) return false;
  return INFERRED_FROM_CONTEXT_SENTINELS.some(s => src.includes(s));
}

export interface SsmBodyGuardOptions {
  /** Stamped on the report when LLM narrative was unavailable. */
  degradedMode?: boolean;
  /**
   * Override the build-time flag — primarily for tests. When omitted,
   * `isSsmBodyGuardEnabled()` is consulted.
   */
  enabled?: boolean;
}

/**
 * Rewrite body text so that every `[Ref N]` token pointing to an
 * inferred-from-context appendix row is preceded by an `[unverified]`
 * reliability marker (which `walkClaimText` renders as the rose-coloured
 * chip via the existing `ReliabilityBadge` component).
 *
 * Idempotent: tokens already carrying an inline reliability marker
 * (`[verified Ref 3]`, `[inferred Ref 3]`, `[unverified Ref 3]`) are
 * left alone, even if the resolved ref is sentinel-flagged. The LLM's
 * explicit tier always wins over the heuristic.
 *
 * No-op when (degradedMode = false) OR (enabled = false) OR (refs is
 * empty / undefined). Returns the input string by identity in those
 * branches so React memoisation downstream isn't busted.
 */
export function applySsmBodyGuard(
  text: string | undefined | null,
  refs: DataSource[] | undefined,
  opts: SsmBodyGuardOptions,
): string {
  if (!text) return text ?? '';
  if (!opts.degradedMode) return text;
  const enabled = opts.enabled ?? isSsmBodyGuardEnabled();
  if (!enabled) return text;
  if (!refs || refs.length === 0) return text;

  // Scan the string for bare `[Ref N]` tokens. We deliberately
  // re-use the same shape `claimReliability.CLAIM_SPLIT_RE` reads
  // so the downstream parser picks up the inserted `[unverified]`
  // tag transparently. A negative lookbehind avoids double-wrapping
  // when the LLM has already emitted a reliability tier inline.
  // JavaScript engines that support `(?<=…)` have shipped since
  // 2018 (Chrome 62, FF 78, Safari 16.4) — we're targeting ES2022
  // in tsconfig so this is safe.
  const re = /(?<!\[(verified|inferred|unverified)(?:\s+(?:Ref\s+\d+|[^\]]+))?\]\s*)\[Ref\s+(\d+)\]/gi;

  let dirty = false;
  const rewritten = text.replace(re, (match, _tier, nStr) => {
    const n = Number(nStr);
    if (!Number.isFinite(n) || n < 1 || n > refs.length) return match;
    const ref = refs[n - 1];
    if (!isInferredFromContextRef(ref)) return match;
    dirty = true;
    // The space between the chip and the ref pill is intentional —
    // walkClaimText splits on the token boundary; keeping a separator
    // keeps the visual cadence consistent with hand-authored
    // `[inferred Ref 3]` strings.
    return `[unverified inferred-from-context] ${match}`;
  });

  // Preserve object identity when nothing matched (memo-friendly).
  return dirty ? rewritten : text;
}

/**
 * Convenience: short status string for the report-view banner when
 * SSM-BODY-GUARD is active. The banner already exists for
 * `degradedMode`; callers append this suffix when the guard rewrote
 * one or more tokens so readers know what the rose chips mean.
 */
export function describeSsmBodyGuardState(opts: SsmBodyGuardOptions): string | null {
  if (!opts.degradedMode) return null;
  const enabled = opts.enabled ?? isSsmBodyGuardEnabled();
  if (!enabled) return null;
  return 'Unverified-claim chips below mark citations with no provider evidence.';
}

/**
 * The report's narrative-bearing string fields, mirrored from R-66's
 * `rewriteProvenanceGapTokens` (citationIntegrityVerifier.ts) so the SSM
 * body guard rewrites the SAME surface the citation-integrity gate already
 * covers — top-level string fields, `sectionContent`, `sections[].body`,
 * `claims[].text`, and `markdown`. Keeping the two walkers in lockstep means
 * a `[Ref N]` token gets chipped no matter which field it lives in.
 */
interface ReportWithNarrativeText extends SiteRagReport {
  markdown?: string;
  sectionContent?: Record<string, string>;
  sections?: Array<{ sectionId: string; body: string } & Record<string, unknown>>;
}

/**
 * H-3 — REPORT-GENERATION-time application of the SSM body guard.
 *
 * Walks every narrative string field of the report and rewrites bare
 * `[Ref N]` tokens that resolve to an inferred-from-context appendix row
 * (`report.references[N-1]`) so they are preceded by the
 * `[unverified inferred-from-context]` reliability marker. The marker is
 * the existing `claimReliability` convention — `walkClaimText` renders it
 * as the rose "unverified" badge with no renderer change required.
 *
 * Gating (ALL must hold, else the report object is returned BY IDENTITY):
 *   - `report.degradedMode` is true (LLM narrative ran without full evidence)
 *   - the guard flag is enabled (`SSM_BODY_GUARD` / `VITE_SSM_BODY_GUARD`;
 *     overridable via `opts.enabled` for tests)
 *   - `report.references` contains at least one inferred-from-context row
 *
 * With the flag OFF the function is a byte-identical no-op: it returns the
 * exact input report reference (no clone, no field touched). It NEVER
 * fabricates, hides, or removes a citation — only annotates an unbacked one.
 */
export function applySsmBodyGuardToReport(
  report: SiteRagReport,
  opts: SsmBodyGuardOptions = {},
): SiteRagReport {
  const degradedMode = opts.degradedMode ?? report.degradedMode ?? false;
  if (!degradedMode) return report;
  const enabled = opts.enabled ?? isSsmBodyGuardEnabled();
  if (!enabled) return report;

  const refs = report.references;
  if (!refs || refs.length === 0) return report;
  // Conservative short-circuit: if NO ref is inferred-from-context there is
  // nothing to chip, so preserve object identity (memo-friendly, byte-equal).
  if (!refs.some(isInferredFromContextRef)) return report;

  const rewriteOpts: SsmBodyGuardOptions = { degradedMode: true, enabled: true };
  const rewrite = (text: string): string => applySsmBodyGuard(text, refs, rewriteOpts);

  const out = { ...report } as ReportWithNarrativeText;
  const r = out as unknown as Record<string, unknown>;
  let dirty = false;

  for (const [key, val] of Object.entries(r)) {
    if (typeof val === 'string' && val.includes('[')) {
      const next = rewrite(val);
      if (next !== val) {
        r[key] = next;
        dirty = true;
      }
    }
  }

  if (out.sectionContent) {
    let sectionDirty = false;
    const next: Record<string, string> = {};
    for (const [key, val] of Object.entries(out.sectionContent)) {
      if (typeof val === 'string') {
        const rewritten = rewrite(val);
        if (rewritten !== val) sectionDirty = true;
        next[key] = rewritten;
      } else {
        next[key] = val;
      }
    }
    if (sectionDirty) {
      out.sectionContent = next;
      dirty = true;
    }
  }

  if (out.sections) {
    let sectionsDirty = false;
    const next = out.sections.map((s) => {
      if (typeof s.body !== 'string') return s;
      const rewritten = rewrite(s.body);
      if (rewritten === s.body) return s;
      sectionsDirty = true;
      return { ...s, body: rewritten };
    });
    if (sectionsDirty) {
      out.sections = next;
      dirty = true;
    }
  }

  if (out.claims) {
    let claimsDirty = false;
    const next = out.claims.map((claim) => {
      const rewritten = rewrite(claim.text);
      if (rewritten === claim.text) return claim;
      claimsDirty = true;
      return { ...claim, text: rewritten };
    });
    if (claimsDirty) {
      out.claims = next;
      dirty = true;
    }
  }

  // Nothing matched after the walk (e.g. the inferred ref is never cited in
  // body prose) — return the original by identity so downstream memoisation
  // and the flag-off byte-equality guarantee both hold.
  return dirty ? out : report;
}
